import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight, Quote, Phone, MessageCircle } from "lucide-react";
import BookingBar from "@/components/BookingBar";
import SectionHeading from "@/components/SectionHeading";
import RoomCard from "@/components/RoomCard";
import EditorialMarquee from "@/components/Marquee";
import CTABanner from "@/components/CTABanner";
import { Reveal, MaskedLine } from "@/components/Reveal";
import { FACILITY_ICONS } from "@/lib/icons";
import { api } from "@/lib/api";
import { SITE, waLink } from "@/lib/site";

const HERO_IMG = "https://customer-assets-gfyr7b9c.emergentagent.net/job_somnath-coastal-stay/artifacts/u720enbm_IMG_0885.jpeg";

const WHY_US = [
  { title: "Comfortable Rooms", desc: "Relax in clean, thoughtfully maintained accommodation - every room facing the Arabian Sea." },
  { title: "Warm Hospitality", desc: "Friendly, personalized service designed to make every guest feel welcome." },
  { title: "Convenient Location", desc: "Easy access to Somnath Temple and the coast, with a complimentary temple shuttle." },
  { title: "Best Direct Booking", desc: "Book directly through our official website with a simple, transparent process." },
  { title: "Memorable Gujarat Stay", desc: "Experience Gujarat with a comfortable seafront base for your journey." },
];

export default function Home() {
  const [rooms, setRooms] = useState([]);
  const [facilities, setFacilities] = useState([]);
  const [attractions, setAttractions] = useState([]);
  const heroRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: heroRef, offset: ["start start", "end start"] });
  const bgY = useTransform(scrollYProgress, [0, 1], ["0%", "22%"]);

  useEffect(() => {
    document.title = "Sagar Darshan Guest House, Somnath Gujarat | Seafront Stay near Somnath Temple";
    api.rooms().then(setRooms).catch(() => {});
    api.facilities().then(setFacilities).catch(() => {});
    api.attractions().then(setAttractions).catch(() => {});
  }, []);

  return (
    <div data-testid="home-page">
      {/* HERO */}
      <section ref={heroRef} className="grain relative flex min-h-screen flex-col justify-end overflow-hidden bg-navy">
        <motion.div style={{ y: bgY }} className="absolute inset-0">
          <div className="h-full w-full animate-slow-zoom bg-cover bg-center" style={{ backgroundImage: `url('${HERO_IMG}')` }} />
        </motion.div>
        <div className="absolute inset-0 bg-gradient-to-t from-navy via-navy/45 to-navy/30" />

        <div className="relative mx-auto w-full max-w-7xl px-5 pb-10 pt-40 lg:px-10">
          <MaskedLine>
            <span data-testid="official-website-badge" className="inline-block border border-gold/60 bg-navy/70 px-5 py-2.5 text-[10px] font-semibold uppercase tracking-[0.35em] text-gold backdrop-blur-sm">
              Official Website
            </span>
          </MaskedLine>
          <h1 className="mt-6 font-display text-5xl leading-[1.04] tracking-tight text-sand sm:text-6xl lg:text-7xl">
            <MaskedLine delay={0.15}>SAGAR DARSHAN</MaskedLine>
            <MaskedLine delay={0.3}>
              <span className="italic text-gold">Guest House</span>
            </MaskedLine>
          </h1>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.7, duration: 0.8 }}
            className="mt-5 text-xs font-medium uppercase tracking-[0.3em] text-sand/70"
          >
            Prabhas Patan · Somnath · Gujarat
          </motion.p>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1, duration: 0.8 }}
            className="mt-9 flex flex-wrap gap-4"
          >
            <Link to="/book" data-testid="hero-book-stay-btn" className="btn-primary">Book Your Stay</Link>
            <Link to="/rooms" data-testid="hero-explore-rooms-btn" className="btn-outline-light">Explore Rooms</Link>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1.1, duration: 0.8 }}
            className="mt-4 flex flex-wrap gap-3"
          >
            <a
              href={`tel:${SITE.phoneHref}`}
              data-testid="hero-call-btn"
              className="inline-flex items-center gap-2.5 border border-white/25 bg-ocean/85 px-6 py-3.5 text-xs font-semibold uppercase tracking-[0.15em] text-white backdrop-blur-md transition-[transform,background-color] duration-300 hover:-translate-y-0.5 hover:bg-ocean"
            >
              <Phone size={15} strokeWidth={1.8} /> Call {SITE.phoneDisplay}
            </a>
            <a
              href={waLink("Hello Sagar Darshan Guest House, I would like to enquire about a stay.")}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="hero-whatsapp-btn"
              className="inline-flex items-center gap-2.5 bg-[#25D366] px-6 py-3.5 text-xs font-semibold uppercase tracking-[0.15em] text-white transition-[transform,background-color] duration-300 hover:-translate-y-0.5 hover:bg-[#1EBE59]"
            >
              <MessageCircle size={15} strokeWidth={1.8} /> WhatsApp Us
            </a>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1.2, duration: 0.8 }}
            className="mt-12"
          >
            <BookingBar />
          </motion.div>
        </div>
      </section>

      {/* DIRECT BOOKING PROMO */}
      <section data-testid="direct-booking-banner" className="bg-gold">
        <div className="mx-auto flex max-w-7xl flex-col items-center justify-between gap-6 px-5 py-10 md:flex-row lg:px-10">
          <div>
            <p className="font-display text-2xl tracking-tight text-navy sm:text-3xl">Book Direct &amp; Get the Best Available Rate</p>
            <p className="mt-2 text-sm text-navy/70">Reserve directly through our official website and enjoy exclusive direct-booking benefits.</p>
          </div>
          <Link to="/book" data-testid="book-direct-btn" className="btn-outline-dark shrink-0 !border-navy">Book Direct</Link>
        </div>
      </section>

      <EditorialMarquee />

      {/* ABOUT PREVIEW */}
      <section className="mx-auto max-w-7xl px-5 py-24 lg:px-10 lg:py-32">
        <div className="grid items-center gap-14 lg:grid-cols-2">
          <div>
            <SectionHeading number="01" overline="Our Story" title="Your Home Away From Home" />
            <Reveal delay={0.15}>
              <p className="mt-7 leading-relaxed text-navy/70">
                Set on the seafront at Prabhas Patan, Sagar Darshan Guest House - operated by Shree Somnath Trust - offers clean, comfortable accommodation with the Arabian Sea at your doorstep and the sacred Somnath Temple just moments away.
              </p>
              <p className="mt-4 leading-relaxed text-navy/70">
                Every room faces the sea. Wake to the sound of waves, take our complimentary shuttle for darshan, and return to warm, personalized service that has welcomed pilgrims, families and travellers for years.
              </p>
            </Reveal>
            <Reveal delay={0.25}>
              <ul className="mt-7 grid grid-cols-2 gap-x-6 gap-y-3 text-sm text-navy/80">
                {["Sea views from every room", "Warm hospitality", "Convenient location", "Clean & well-maintained", "Family-friendly stay", "Complimentary temple shuttle"].map((f) => (
                  <li key={f} className="flex items-center gap-2.5">
                    <span className="h-1.5 w-1.5 rotate-45 bg-gold-dark" /> {f}
                  </li>
                ))}
              </ul>
              <Link to="/about" data-testid="discover-story-btn" className="btn-outline-dark mt-10">
                Discover Our Story <ArrowRight size={14} />
              </Link>
            </Reveal>
          </div>
          <Reveal delay={0.2} className="relative">
            <div className="absolute -left-4 -top-4 h-full w-full border border-gold" />
            <img
              src="https://customer-assets-gfyr7b9c.emergentagent.net/job_somnath-coastal-stay/artifacts/97epeuxe_IMG_0894.jpeg"
              alt="Sea view from a balcony at Sagar Darshan Guest House, Somnath, Gujarat"
              loading="lazy"
              className="relative aspect-[4/5] w-full object-cover"
            />
            <div className="absolute -bottom-6 -right-4 bg-navy px-7 py-5 text-sand shadow-2xl sm:-right-6">
              <p className="font-display text-3xl italic text-gold">Seafront</p>
              <p className="mt-1 text-[10px] uppercase tracking-[0.25em] text-sand/60">Prabhas Patan · Somnath</p>
            </div>
          </Reveal>
        </div>
      </section>

      {/* ROOMS */}
      <section className="bg-white py-24 lg:py-32">
        <div className="mx-auto max-w-7xl px-5 lg:px-10">
          <div className="flex flex-wrap items-end justify-between gap-6">
            <SectionHeading number="02" overline="Accommodation" title="Our Rooms" />
            <Reveal delay={0.1}>
              <Link to="/rooms" data-testid="view-all-rooms-btn" className="btn-outline-dark !py-3">View All Rooms</Link>
            </Reveal>
          </div>
          <div className="mt-14 grid gap-8 md:grid-cols-2 lg:grid-cols-3">
            {rooms.map((room, i) => <RoomCard key={room.id} room={room} index={i} />)}
          </div>
        </div>
      </section>

      {/* FACILITIES */}
      <section className="grain relative bg-navy py-24 lg:py-32">
        <div className="mx-auto max-w-7xl px-5 lg:px-10">
          <SectionHeading number="03" overline="Facilities" title="Everything You Need for a Comfortable Stay" dark />
          <div className="mt-14 grid grid-cols-2 gap-px bg-white/10 md:grid-cols-3 lg:grid-cols-4">
            {facilities.slice(0, 8).map((f, i) => {
              const Icon = FACILITY_ICONS[f.icon] || FACILITY_ICONS.sparkles;
              return (
                <Reveal key={f.title} delay={i * 0.06}>
                  <div data-testid={`facility-${f.icon}`} className="group flex h-full flex-col gap-3 bg-navy p-7 transition-colors duration-500 hover:bg-ocean">
                    <Icon size={26} strokeWidth={1.2} className="text-gold transition-transform duration-500 group-hover:-translate-y-1" />
                    <p className="font-display text-lg text-sand">{f.title}</p>
                    <p className="text-xs leading-relaxed text-sand/50">{f.desc}</p>
                  </div>
                </Reveal>
              );
            })}
          </div>
        </div>
      </section>

      {/* WHY CHOOSE US */}
      <section className="bg-aqua-light py-24 lg:py-32">
        <div className="mx-auto max-w-7xl px-5 lg:px-10">
        <SectionHeading number="04" overline="Why Choose Us" title="Why Stay With Sagar Darshan?" center />
        <div className="mt-14 grid gap-6 sm:grid-cols-2 lg:grid-cols-5">
          {WHY_US.map((w, i) => (
            <Reveal key={w.title} delay={i * 0.08}>
              <div data-testid={`why-card-${i}`} className="group flex h-full flex-col border border-navy/10 bg-white p-7 transition-[transform,box-shadow] duration-500 hover:-translate-y-2 hover:shadow-xl hover:shadow-navy/10">
                <span className="font-display text-4xl italic text-gold-dark">0{i + 1}</span>
                <p className="mt-5 font-display text-lg leading-snug text-navy">{w.title}</p>
                <p className="mt-3 text-xs leading-relaxed text-navy/60">{w.desc}</p>
              </div>
            </Reveal>
          ))}
        </div>
        </div>
      </section>

      {/* EXPLORE GUJARAT */}
      <section className="bg-white py-24 lg:py-32">
        <div className="mx-auto max-w-7xl px-5 lg:px-10">
          <div className="flex flex-wrap items-end justify-between gap-6">
            <SectionHeading number="05" overline="The Destination" title="Explore Gujarat" />
            <Reveal delay={0.1}>
              <Link to="/location" data-testid="explore-nearby-btn" className="btn-outline-dark !py-3">Explore Nearby</Link>
            </Reveal>
          </div>
          <div className="mt-14 grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
            {attractions.map((a, i) => (
              <Reveal key={a.name} delay={i * 0.08}>
                <div data-testid={`attraction-card-${i}`} className="group relative aspect-[3/4] overflow-hidden">
                  <img src={a.image} alt={`${a.name} near Sagar Darshan Guest House, Somnath`} loading="lazy"
                    className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-105" />
                  <div className="absolute inset-0 bg-gradient-to-t from-navy/95 via-navy/30 to-transparent" />
                  <div className="absolute inset-x-0 bottom-0 p-6">
                    <span className="text-[10px] font-semibold uppercase tracking-[0.2em] text-gold">{a.distance}</span>
                    <p className="mt-2 font-display text-xl leading-snug text-sand">{a.name}</p>
                    <p className="mt-2 text-xs leading-relaxed text-sand/70">{a.desc}</p>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* REVIEWS */}
      <section className="grain relative bg-navy py-24 lg:py-32">
        <div className="mx-auto max-w-7xl px-5 lg:px-10">
          <SectionHeading number="06" overline="Guest Reviews" title="What Our Guests Say" dark center />
          <div className="mt-14 grid gap-6 md:grid-cols-3">
            {[1, 2, 3].map((n) => (
              <Reveal key={n} delay={n * 0.08}>
                <div data-testid={`review-placeholder-${n}`} className="flex h-full flex-col items-center justify-center border border-dashed border-gold/30 p-10 text-center">
                  <Quote size={26} strokeWidth={1.2} className="text-gold/60" />
                  <p className="mt-5 font-display text-lg italic text-sand/70">Genuine guest reviews coming soon</p>
                  <p className="mt-3 text-xs leading-relaxed text-sand/40">
                    Verified reviews from our guests will appear here. Stay with us and share your experience.
                  </p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <CTABanner />
    </div>
  );
}
